import { useEffect, useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import * as XLSX from 'xlsx'

import { getArtifacts } from '../lib/server-functions'
import {
  trackPageView,
  trackExcelDownload,
  trackFilterApplied,
  trackArtifactClick,
  trackSessionStart,
} from '../lib/sentry-tracking'

import type { TeamForgeArtifact } from '../lib/teamforge'
import type {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  SortingState,
} from '@tanstack/react-table'

const arrIncludes: FilterFn<any> = (row, columnId, filterValue) => {
  if (
    filterValue === undefined ||
    filterValue === '__all__' ||
    filterValue === '' ||
    (Array.isArray(filterValue) && filterValue.length === 0)
  ) {
    return true
  }
  const rowValue = row.getValue(columnId)
  const filterValues = Array.isArray(filterValue) ? filterValue : [filterValue]
  const normalizedRowValue = rowValue == null ? '' : String(rowValue).trim()
  const normalizedFilterValues = filterValues.map((fv) => String(fv).trim())

  if (Array.isArray(rowValue)) {
    return normalizedFilterValues.some((fv) =>
      rowValue.some((rv) => String(rv).trim() === fv),
    )
  }
  return normalizedFilterValues.includes(normalizedRowValue)
}

type DateFilterValue =
  | { type: 'after'; date: string }
  | { type: 'before'; date: string }
  | { type: 'between'; from: string; to: string }
  | undefined

const dateFilter: FilterFn<any> = (row, columnId, filterValue: DateFilterValue) => {
  if (!filterValue) return true

  const rowValue = row.getValue(columnId) as string
  if (!rowValue) return false

  try {
    const rowDate = new Date(rowValue)
    if (isNaN(rowDate.getTime())) return false

    rowDate.setHours(0, 0, 0, 0)

    if (filterValue.type === 'after') {
      const filterDate = new Date(filterValue.date)
      filterDate.setHours(0, 0, 0, 0)
      return rowDate.getTime() >= filterDate.getTime()
    }

    if (filterValue.type === 'before') {
      const filterDate = new Date(filterValue.date)
      filterDate.setHours(23, 59, 59, 999)
      return rowDate.getTime() <= filterDate.getTime()
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (filterValue.type === 'between') {
      const fromDate = new Date(filterValue.from)
      fromDate.setHours(0, 0, 0, 0)
      const toDate = new Date(filterValue.to)
      toDate.setHours(23, 59, 59, 999)
      return (
        rowDate.getTime() >= fromDate.getTime() &&
        rowDate.getTime() <= toDate.getTime()
      )
    }
  } catch {
    return false
  }

  return true
}

export const Route = createFileRoute('/home')({
  component: HomePage,
})

const TEAMFORGE_BASE_URL =
  process.env.TEAMFORGE_BASE_URL || 'https://teamforge.iptronix.com'

function HomePage() {
  const router = useRouter()
  const [artifacts, setArtifacts] = useState<Array<TeamForgeArtifact>>([])
  const [loading, setLoading] = useState(true)
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'endDate', desc: false },
    { id: 'priority', desc: true },
  ])
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set())
  const [filterPositions, setFilterPositions] = useState<
    Record<string, { left: number; top: number }>
  >({})

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  useEffect(() => {
    trackPageView('/home')
    trackSessionStart()
  }, [])

  useEffect(() => {
    const loadArtifacts = async () => {
      const token = sessionStorage.getItem('teamforge_token')
      if (!token) {
        router.navigate({ to: '/login' })
        return
      }

      setLoading(true)
      try {
        const result = await getArtifacts({ data: { token } })
        setArtifacts(result.artifacts)

        if (result.statusFilter) {
          setColumnFilters((prev) => {
            const otherFilters = prev.filter((f) => f.id !== 'status')
            return [
              ...otherFilters,
              {
                id: 'status',
                value: result.statusFilter,
              },
            ]
          })
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error)
        if (
          errorMessage.includes('401') ||
          errorMessage.includes('invalid_token') ||
          errorMessage.includes('Token expired')
        ) {
          sessionStorage.removeItem('teamforge_token')
          router.navigate({ to: '/login' })
          return
        }
        console.error('Failed to load artifacts:', error)
      } finally {
        setLoading(false)
      }
    }

    loadArtifacts()
  }, [router])

  const columns: Array<ColumnDef<TeamForgeArtifact>> = [
    {
      accessorKey: 'title',
      header: 'Title',
      size: 200,
      cell: ({ row }) => {
        const artifact = row.original
        if (artifact.id) {
          const artifactUrl = `${TEAMFORGE_BASE_URL}/sf/go/${artifact.id}`
          return (
            <a
              href={artifactUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-cyan-400 hover:text-cyan-300 hover:underline"
              onClick={(e) => {
                e.stopPropagation()
                trackArtifactClick(artifact.id || '')
              }}
              onMouseDown={(e) => {
                e.stopPropagation()
              }}
            >
              {artifact.title}
            </a>
          )
        }
        return <div className="font-medium">{artifact.title}</div>
      },
    },
    {
      accessorKey: 'assignee',
      header: 'Assignee',
      size: 150,
      filterFn: 'includesString',
    },
    {
      accessorKey: 'endDate',
      header: 'End Date',
      size: 120,
      filterFn: 'dateFilter',
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.endDate
        const dateB = rowB.original.endDate
        if (!dateA && !dateB) return 0
        if (!dateA) return 1
        if (!dateB) return -1
        try {
          return new Date(dateA).getTime() - new Date(dateB).getTime()
        } catch {
          return dateA.localeCompare(dateB)
        }
      },
      cell: ({ getValue }) => {
        const value = getValue() as string
        if (!value) return ''
        try {
          return new Date(value).toLocaleDateString('it-IT')
        } catch {
          return value
        }
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      size: 120,
      filterFn: 'arrIncludes',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      filterFn: 'arrIncludes',
    },
    {
      accessorKey: 'estimatedHours',
      header: 'Estimated Hours',
      size: 130,
      cell: ({ getValue }) => {
        const value = getValue() as number
        return value || value === 0 ? String(value) : ''
      },
    },
    {
      accessorKey: 'actualHours',
      header: 'Actual Hours',
      size: 120,
      cell: ({ getValue }) => {
        const value = getValue() as number
        return value || value === 0 ? String(value) : ''
      },
    },
    {
      accessorKey: 'remainingHours',
      header: 'Remaining Hours',
      size: 140,
      cell: ({ getValue }) => {
        const value = getValue() as number
        return value || value === 0 ? String(value) : ''
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 200,
      filterFn: 'includesString',
      cell: ({ getValue }) => {
        const value = getValue() as string
        return (
          <div className="max-w-md truncate" title={value}>
            {value}
          </div>
        )
      },
    },
    {
      accessorKey: 'projectTitle',
      header: 'Project',
      size: 150,
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const artifact = row.original
        const onClick = (e: React.MouseEvent) => {
          e.stopPropagation()
        }
        const onMouseDown = (e: React.MouseEvent) => {
          e.stopPropagation()
        }
        if (artifact.projectId) {
          const projectUrl = `${TEAMFORGE_BASE_URL}/sf/go/${artifact.projectId}`
          return (
            <a
              href={projectUrl}
              onClick={onClick}
              onMouseDown={onMouseDown}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 hover:underline"
            >
              {artifact.projectTitle}
            </a>
          )
        }
        return <span>{artifact.projectTitle}</span>
      },
    },
  ]

  const table = useReactTable({
    data: artifacts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    filterFns: {
      arrIncludes,
      dateFilter,
    },
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater) => {
      setColumnFilters((prev) => {
        const next =
          typeof updater === 'function' ? updater(prev) : updater
        const changedFilter = next.find(
          (f, idx) => !prev[idx] || prev[idx].value !== f.value,
        )
        if (changedFilter) {
          trackFilterApplied(changedFilter.id, changedFilter.value)
        }
        return next
      })
    },
  })

  const getUniqueValues = (columnId: string): Array<string> => {
    const values = new Set<string>()
    artifacts.forEach((artifact) => {
      const value = artifact[columnId as keyof TeamForgeArtifact]
      if (value != null && value !== '') {
        values.add(String(value).trim())
      }
    })
    return Array.from(values).sort()
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (
        !target.closest('.filter-dropdown') &&
        !target.closest('[data-filter-button]') &&
        !target.closest('a[target="_blank"]')
      ) {
        setOpenFilters(new Set())
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const updatePositions = () => {
      const positions: Record<string, { left: number; top: number }> = {}
      openFilters.forEach((columnId) => {
        const button = document.querySelector(
          `[data-filter-button="${columnId}"]`,
        )
        if (button) {
          const rect = (button as HTMLElement).getBoundingClientRect()
          positions[columnId] = {
            left: rect.left,
            top: rect.bottom + 4,
          }
        }
      })
      setFilterPositions(positions)
    }
    if (openFilters.size > 0) {
      updatePositions()
      window.addEventListener('scroll', updatePositions, true)
      window.addEventListener('resize', updatePositions)
      return () => {
        window.removeEventListener('scroll', updatePositions, true)
        window.removeEventListener('resize', updatePositions)
      }
    }
  }, [openFilters])

  const handleDownloadExcel = () => {
    const data = artifacts.map((artifact) => ({
      Title: artifact.title,
      Assignee: artifact.assignee,
      'End Date': artifact.endDate,
      Priority: artifact.priority,
      Status: artifact.status,
      'Estimated Hours': artifact.estimatedHours,
      'Actual Hours': artifact.actualHours,
      'Remaining Hours': artifact.remainingHours,
      Description: artifact.description,
      Project: artifact.projectTitle,
      ArtifactID: artifact.id,
      ProjectID: artifact.projectId,
    }))

    const worksheet = XLSX.utils.json_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Artifacts')
    XLSX.writeFile(workbook, 'teamforge_artifacts.xlsx')
    trackExcelDownload(artifacts.length)
  }

  const filteredCount = table.getFilteredRowModel().rows.length

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="p-6">
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold mb-2">Artifacts</h1>
              <p className="text-gray-400 mb-2">
                Showing {filteredCount} of {artifacts.length} artifacts
              </p>
            </div>
            <button
              onClick={handleDownloadExcel}
              className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || artifacts.length === 0}
            >
              Download Excel
            </button>
          </div>
          {loading && (
            <div className="mt-4 text-sm text-gray-400">
              Loading artifacts...
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="rounded-lg border border-slate-700">
              <table className="min-w-full divide-y divide-slate-700">
                <thead className="bg-slate-800">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const column = header.column
                        const uniqueValues = getUniqueValues(column.id)
                        const filterValue = column.getFilterValue() as
                          | string
                          | Array<string>
                          | DateFilterValue
                          | undefined

                        return (
                          <th
                            key={header.id}
                            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-300"
                            style={{ width: header.getSize() }}
                          >
                            <div className="space-y-2">
                              <div
                                className="flex items-center gap-2 cursor-pointer select-none"
                                onClick={column.getToggleSortingHandler()}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                                {column.getIsSorted() === 'asc' && (
                                  <span className="text-cyan-500">↑</span>
                                )}
                                {column.getIsSorted() === 'desc' && (
                                  <span className="text-cyan-500">↓</span>
                                )}
                              </div>
                              {column.getCanFilter() && (
                                <div
                                  className="relative filter-dropdown"
                                  style={{ position: 'relative', zIndex: 1 }}
                                >
                                  {column.id === 'endDate' ? (
                                    <>
                                      <button
                                        type="button"
                                        data-filter-button={column.id}
                                        className="w-full rounded border border-slate-600 bg-slate-700 px-2 py-1 text-xs text-white focus:border-cyan-500 focus:outline-none text-left flex items-center justify-between"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          const button = e.currentTarget
                                          const rect =
                                            button.getBoundingClientRect()
                                          setFilterPositions((prev) => ({
                                            ...prev,
                                            [column.id]: {
                                              left: rect.left,
                                              top: rect.bottom + 4,
                                            },
                                          }))
                                          const newOpen = new Set(openFilters)
                                          if (newOpen.has(column.id)) {
                                            newOpen.delete(column.id)
                                          } else {
                                            newOpen.add(column.id)
                                          }
                                          setOpenFilters(newOpen)
                                        }}
                                      >
                                        <span className="truncate">
                                          {(() => {
                                            const dateFilter = filterValue as DateFilterValue
                                            if (!dateFilter) return 'All'
                                            if (dateFilter.type === 'after')
                                              return `After ${new Date(dateFilter.date).toLocaleDateString('it-IT')}`
                                            if (dateFilter.type === 'before')
                                              return `Before ${new Date(dateFilter.date).toLocaleDateString('it-IT')}`
                                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                                            if (dateFilter.type === 'between')
                                              return `${new Date(dateFilter.from).toLocaleDateString('it-IT')} - ${new Date(dateFilter.to).toLocaleDateString('it-IT')}`
                                            return 'All'
                                          })()}
                                        </span>
                                        <span className="ml-2">
                                          {openFilters.has(column.id)
                                            ? '▲'
                                            : '▼'}
                                        </span>
                                      </button>
                                      {openFilters.has(column.id) && (
                                        <div
                                          className="fixed filter-dropdown z-[9999] w-64 bg-slate-800 border border-slate-600 rounded shadow-lg"
                                          onClick={(e) => e.stopPropagation()}
                                          style={{
                                            left: `${filterPositions[column.id].left}px`,
                                            top: `${filterPositions[column.id].top}px`,
                                          }}
                                        >
                                          <div className="p-3 space-y-3">
                                            <select
                                              className="w-full rounded border border-slate-600 bg-slate-700 px-2 py-1 text-xs text-white focus:border-cyan-500 focus:outline-none"
                                              value={
                                                (filterValue as DateFilterValue)?.type || ''
                                              }
                                              onChange={(e) => {
                                                const type = e.target.value
                                                if (type === '') {
                                                  column.setFilterValue(undefined)
                                                } else if (type === 'after' || type === 'before') {
                                                  column.setFilterValue({
                                                    type,
                                                    date: '',
                                                  } as DateFilterValue)
                                                } else if (type === 'between') {
                                                  column.setFilterValue({
                                                    type: 'between',
                                                    from: '',
                                                    to: '',
                                                  } as DateFilterValue)
                                                }
                                              }}
                                            >
                                              <option value="">All</option>
                                              <option value="after">After (including)</option>
                                              <option value="before">Before (including)</option>
                                              <option value="between">Between</option>
                                            </select>
                                            {(filterValue as DateFilterValue)?.type === 'after' && (
                                              <input
                                                type="date"
                                                className="w-full rounded border border-slate-600 bg-slate-700 px-2 py-1 text-xs text-white focus:border-cyan-500 focus:outline-none"
                                                value={
                                                  (filterValue as DateFilterValue)?.date || ''
                                                }
                                                onChange={(e) => {
                                                  column.setFilterValue({
                                                    type: 'after',
                                                    date: e.target.value,
                                                  } as DateFilterValue)
                                                }}
                                              />
                                            )}
                                            {(filterValue as DateFilterValue)?.type === 'before' && (
                                              <input
                                                type="date"
                                                className="w-full rounded border border-slate-600 bg-slate-700 px-2 py-1 text-xs text-white focus:border-cyan-500 focus:outline-none"
                                                value={
                                                  (filterValue as DateFilterValue)?.date || ''
                                                }
                                                onChange={(e) => {
                                                  column.setFilterValue({
                                                    type: 'before',
                                                    date: e.target.value,
                                                  } as DateFilterValue)
                                                }}
                                              />
                                            )}
                                            {(filterValue as DateFilterValue)?.type === 'between' && (
                                              <div className="space-y-2">
                                                <div>
                                                  <label className="block text-xs text-slate-300 mb-1">
                                                    From:
                                                  </label>
                                                  <input
                                                    type="date"
                                                    className="w-full rounded border border-slate-600 bg-slate-700 px-2 py-1 text-xs text-white focus:border-cyan-500 focus:outline-none"
                                                    value={
                                                      (filterValue as DateFilterValue)?.from || ''
                                                    }
                                                    onChange={(e) => {
                                                      const current = filterValue as DateFilterValue
                                                      column.setFilterValue({
                                                        type: 'between',
                                                        from: e.target.value,
                                                        to: current?.to || '',
                                                      } as DateFilterValue)
                                                    }}
                                                  />
                                                </div>
                                                <div>
                                                  <label className="block text-xs text-slate-300 mb-1">
                                                    To:
                                                  </label>
                                                  <input
                                                    type="date"
                                                    className="w-full rounded border border-slate-600 bg-slate-700 px-2 py-1 text-xs text-white focus:border-cyan-500 focus:outline-none"
                                                    value={
                                                      (filterValue as DateFilterValue)?.to || ''
                                                    }
                                                    onChange={(e) => {
                                                      const current = filterValue as DateFilterValue
                                                      column.setFilterValue({
                                                        type: 'between',
                                                        from: current?.from || '',
                                                        to: e.target.value,
                                                      } as DateFilterValue)
                                                    }}
                                                  />
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  ) : [
                                    'status',
                                    'priority',
                                    'projectTitle',
                                  ].includes(column.id) ? (
                                    <>
                                      <button
                                        type="button"
                                        data-filter-button={column.id}
                                        className="w-full rounded border border-slate-600 bg-slate-700 px-2 py-1 text-xs text-white focus:border-cyan-500 focus:outline-none text-left flex items-center justify-between"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          const button = e.currentTarget
                                          const rect =
                                            button.getBoundingClientRect()
                                          setFilterPositions((prev) => ({
                                            ...prev,
                                            [column.id]: {
                                              left: rect.left,
                                              top: rect.bottom + 4,
                                            },
                                          }))
                                          const newOpen = new Set(openFilters)
                                          if (newOpen.has(column.id)) {
                                            newOpen.delete(column.id)
                                          } else {
                                            newOpen.add(column.id)
                                          }
                                          setOpenFilters(newOpen)
                                        }}
                                      >
                                        <span className="truncate">
                                          {Array.isArray(filterValue) &&
                                          filterValue.length > 0
                                            ? `${filterValue.length} selected`
                                            : 'All'}
                                        </span>
                                        <span className="ml-2">
                                          {openFilters.has(column.id)
                                            ? '▲'
                                            : '▼'}
                                        </span>
                                      </button>
                                      {openFilters.has(column.id) && (
                                        <div
                                          className="fixed filter-dropdown z-[9999] w-48 max-h-64 overflow-y-auto bg-slate-800 border border-slate-600 rounded shadow-lg"
                                          onClick={(e) => e.stopPropagation()}
                                          style={{
                                            left: `${filterPositions[column.id].left}px`,
                                            top: `${filterPositions[column.id].top}px`,
                                          }}
                                        >
                                          <div className="p-2 space-y-1">
                                            <label className="flex items-center px-2 py-1 hover:bg-slate-700 rounded cursor-pointer">
                                              <input
                                                type="checkbox"
                                                className="mr-2 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
                                                checked={
                                                  !Array.isArray(filterValue) ||
                                                  (() => {
                                                    const normalizedFilterValues = filterValue.map((v) =>
                                                      String(v).trim(),
                                                    )
                                                    const normalizedUniqueValues = uniqueValues.map((v) =>
                                                      String(v).trim(),
                                                    )
                                                    return (
                                                      normalizedFilterValues.length ===
                                                        normalizedUniqueValues.length &&
                                                      normalizedUniqueValues.every((v) =>
                                                        normalizedFilterValues.includes(v),
                                                      )
                                                    )
                                                  })()
                                                }
                                                onChange={(e) => {
                                                  if (e.target.checked) {
                                                    column.setFilterValue(
                                                      uniqueValues,
                                                    )
                                                  } else {
                                                    column.setFilterValue(
                                                      undefined,
                                                    )
                                                  }
                                                }}
                                              />
                                              <span className="text-xs text-white">
                                                (Select All)
                                              </span>
                                            </label>
                                            <div className="border-t border-slate-600 my-1"></div>
                                            {uniqueValues.map((value) => {
                                              const selectedValues =
                                                Array.isArray(filterValue)
                                                  ? filterValue
                                                  : []
                                              const normalizedValue = String(value).trim()
                                              const isChecked = selectedValues.some(
                                                (v) => String(v).trim() === normalizedValue,
                                              )
                                              return (
                                                <label
                                                  key={value}
                                                  className="flex items-center px-2 py-1 hover:bg-slate-700 rounded cursor-pointer"
                                                >
                                                  <input
                                                    type="checkbox"
                                                    className="mr-2 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                      const currentValues =
                                                        Array.isArray(
                                                          filterValue,
                                                        )
                                                          ? filterValue
                                                          : []
                                                      if (e.target.checked) {
                                                        if (!isChecked) {
                                                          column.setFilterValue([
                                                            ...currentValues,
                                                            value,
                                                          ])
                                                        }
                                                      } else {
                                                        const newValues =
                                                          currentValues.filter(
                                                            (v) => String(v).trim() !== normalizedValue,
                                                          )
                                                        column.setFilterValue(
                                                          newValues.length > 0
                                                            ? newValues
                                                            : undefined,
                                                        )
                                                      }
                                                    }}
                                                  />
                                                  <span className="text-xs text-white truncate">
                                                    {value}
                                                  </span>
                                                </label>
                                              )
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <input
                                      type="text"
                                      className="w-full rounded border border-slate-600 bg-slate-700 px-2 py-1 text-xs text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
                                      placeholder="Filter..."
                                      value={String(filterValue || '')}
                                      onChange={(e) =>
                                        column.setFilterValue(e.target.value)
                                      }
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </th>
                        )
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-slate-700 bg-slate-900">
                  {table.getRowModel().rows.map((row, idx) => (
                    <tr
                      key={row.id}
                      className={`${
                        idx % 2 === 0 ? 'bg-slate-900' : 'bg-slate-800'
                      } hover:bg-slate-700 transition-colors`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-4 py-3 text-sm text-slate-200"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
